import { AppTopBar, CreateEventButton, TabCountBadge } from '@/components/ui/AppTopBar';
import { TabSwipeNavigationLayer } from '@/components/ui/TabSwipeNavigationLayer';
import { useIsDesktop } from '@/hooks/useBreakpoint';
import { TAB_CONFIG } from '@/lib/navigation/navConfig';
import type { TabRouteName } from '@/lib/navigation/navConfig';
import { trackTabSwitched } from '@/lib/analytics/posthog';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { showAccountContextSheet } from '@/features/workspaces/useAccountContextSheetStore';
import { triggerSelectionHaptic } from '@/lib/platform/haptics';
import { formatBadgeCountLabel, setAppIconBadgeCount } from '@/lib/notifications/notifications';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { APP_TAB_BAR_CONTENT_HEIGHT } from '@/lib/useAppTabBarHeight';
import { isReadOnlyMarketAccess } from '@/features/market/marketAccess';
import {
  getPulseAlertCount,
  getUnreadPulseAlertCount,
  useNotifications,
} from '@/features/pulse/useNotifications';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import type { ParamListBase, TabNavigationState } from '@react-navigation/native';
import { router, Tabs, usePathname, useSegments } from 'expo-router';
import * as React from 'react';
import { Platform, Pressable, Text, View } from 'react-native';
import type { ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const TAB_BAR_UNDERLAY_ROOT_SEGMENTS = new Set([
  'event',
  'events',
  'followers',
  'notifications',
  'privacy-policy',
  'profiles',
  'search',
  'settings',
  'settings-notifications',
  'settings-privacy',
  'terms',
  'venues',
  'workspaces',
]);

function shouldKeepAppTabBarMounted({
  isTabRoute,
  rootSegment,
}: {
  isTabRoute: boolean;
  rootSegment?: string;
}) {
  if (isTabRoute) {
    return true;
  }

  return Boolean(rootSegment && TAB_BAR_UNDERLAY_ROOT_SEGMENTS.has(rootSegment));
}

// ---------------------------------------------------------------------------
// Shared handler factory
// ---------------------------------------------------------------------------

function makeTabHandler(
  navigation: BottomTabBarProps['navigation'],
  state: BottomTabBarProps['state'],
  route: BottomTabBarProps['state']['routes'][number]
) {
  return {
    onPress: () => {
      const routeIndex = state.routes.indexOf(route);
      const willNavigate = state.index !== routeIndex;
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });
      if (event.defaultPrevented) {
        return;
      }
      if (willNavigate) {
        navigation.navigate(route.name, route.params);
      }
    },
    onLongPress: () => navigation.emit({ type: 'tabLongPress', target: route.key }),
  };
}

// ---------------------------------------------------------------------------
// Mobile — anchored bottom bar
// ---------------------------------------------------------------------------

function AppTabBar({
  state,
  descriptors,
  navigation,
  pulseBadgeLabel,
}: BottomTabBarProps & {
  pulseBadgeLabel: string | null;
}) {
  const insets = useSafeAreaInsets();
  const { meaningfulContextCount } = useAccountContext();
  const handleContextLongPress = React.useCallback(() => {
    void triggerSelectionHaptic();
    showAccountContextSheet();
  }, []);
  const session = useAuth((state) => state.session);
  const bottomPadding = Math.max(insets.bottom, 8);
  const canCreate = Boolean(session) && !isReadOnlyMarketAccess(session?.me.profile.market_access);
  const visibleRoutes = state.routes.filter((route) =>
    Object.prototype.hasOwnProperty.call(TAB_CONFIG, route.name)
  );
  const leftRoutes = visibleRoutes.slice(0, 2);
  const rightRoutes = visibleRoutes.slice(2);

  const barStyle: ViewStyle = {
    height: APP_TAB_BAR_CONTENT_HEIGHT + bottomPadding,
    paddingBottom: bottomPadding,
    paddingHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: DESIGN_TOKENS.colors.borderStrong,
    backgroundColor: DESIGN_TOKENS.colors.canvas,
  };

  return (
    <View pointerEvents="box-none" style={barStyle}>
      {leftRoutes.map((route) => {
        const isFocused = state.index === state.routes.indexOf(route);
        const config = TAB_CONFIG[route.name as TabRouteName];
        const { onPress: defaultOnPress, onLongPress: defaultOnLongPress } = makeTabHandler(
          navigation,
          state,
          route
        );
        const label =
          !session && route.name === 'profile'
            ? 'Sign in'
            : route.name === 'profile'
              ? 'You'
              : (config?.label ?? route.name);
        const onPress =
          !session && route.name === 'profile' ? () => router.push('/sign-in') : defaultOnPress;
        const onLongPress =
          session && route.name === 'profile' && meaningfulContextCount > 1
            ? handleContextLongPress
            : defaultOnLongPress;
        return (
          <BottomTabItem
            key={route.key}
            label={label}
            Icon={config?.icon}
            badgeLabel={route.name === 'activity' ? pulseBadgeLabel : null}
            isFocused={isFocused}
            accessibilityLabel={descriptors[route.key].options.tabBarAccessibilityLabel}
            testID={descriptors[route.key].options.tabBarButtonTestID}
            onPress={onPress}
            onLongPress={onLongPress}
          />
        );
      })}

      {canCreate ? (
        <View style={{ width: 72, alignItems: 'center', justifyContent: 'center' }}>
          <MobileCreateButton />
        </View>
      ) : null}

      {rightRoutes.map((route) => {
        const isFocused = state.index === state.routes.indexOf(route);
        const config = TAB_CONFIG[route.name as TabRouteName];
        const { onPress: defaultOnPress, onLongPress: defaultOnLongPress } = makeTabHandler(
          navigation,
          state,
          route
        );
        const label =
          !session && route.name === 'profile'
            ? 'Sign in'
            : route.name === 'profile'
              ? 'You'
              : (config?.label ?? route.name);
        const onPress =
          !session && route.name === 'profile' ? () => router.push('/sign-in') : defaultOnPress;
        const onLongPress =
          session && route.name === 'profile' && meaningfulContextCount > 1
            ? handleContextLongPress
            : defaultOnLongPress;
        return (
          <BottomTabItem
            key={route.key}
            label={label}
            Icon={config?.icon}
            badgeLabel={route.name === 'activity' ? pulseBadgeLabel : null}
            isFocused={isFocused}
            accessibilityLabel={descriptors[route.key].options.tabBarAccessibilityLabel}
            testID={descriptors[route.key].options.tabBarButtonTestID}
            onPress={onPress}
            onLongPress={onLongPress}
          />
        );
      })}
    </View>
  );
}

function BottomTabItem({
  label,
  Icon,
  badgeLabel,
  isFocused,
  accessibilityLabel,
  testID,
  onPress,
  onLongPress,
}: {
  label: string;
  Icon: (typeof TAB_CONFIG)[TabRouteName]['icon'] | undefined;
  badgeLabel?: string | null;
  isFocused: boolean;
  accessibilityLabel?: string;
  testID?: string;
  onPress: () => void;
  onLongPress: () => void;
}) {
  const color = isFocused ? DESIGN_TOKENS.colors.accentStrong : DESIGN_TOKENS.colors.textMuted;

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="tab"
      accessibilityState={isFocused ? { selected: true } : {}}
      testID={testID}
      onPress={onPress}
      onLongPress={onLongPress}
      delayLongPress={220}
      style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <View
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          paddingHorizontal: 12,
          paddingTop: 8,
          paddingBottom: 1,
          borderRadius: 12,
        }}>
        <View style={{ position: 'relative' }}>
          {Icon ? (
            <Icon color={color} size={20} strokeWidth={isFocused ? 2.3 : 1.8} {...({} as any)} />
          ) : null}
          {badgeLabel ? (
            <View
              style={{
                position: 'absolute',
                top: -8,
                right: -14,
              }}>
              <TabCountBadge label={badgeLabel} />
            </View>
          ) : null}
        </View>
        <Text
          style={{
            fontSize: 10,
            lineHeight: 12,
            letterSpacing: 0.4,
            fontFamily: DESIGN_TOKENS.typography.fonts.bodySemiBold,
            color,
          }}>
          {label}
        </Text>
      </View>
    </Pressable>
  );
}

function MobileCreateButton() {
  return <CreateEventButton compact />;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export default function TabsLayout() {
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const isDesktop = useIsDesktop();
  const lastTrackedRouteRef = React.useRef<string | null>(null);
  const segments = useSegments();
  const rootSegment = segments[0];
  const nestedSegment = segments[1];
  const isTabRoute = rootSegment === '(tabs)';
  const activeSwipeRoute =
    isTabRoute && typeof nestedSegment === 'string' && nestedSegment in TAB_CONFIG
      ? (nestedSegment as TabRouteName)
      : null;
  const shouldKeepTabBarMounted = shouldKeepAppTabBarMounted({
    isTabRoute,
    rootSegment,
  });
  const notificationsQuery = useNotifications();
  const notifications = notificationsQuery.data?.notifications ?? [];
  const unreadPulseAlertCount = session
    ? getUnreadPulseAlertCount(notifications)
    : 0;
  const pulseAlertCount = session ? getPulseAlertCount(notifications) : 0;
  const pulseBadgeLabel = formatBadgeCountLabel(pulseAlertCount);

  React.useEffect(() => {
    setAppIconBadgeCount(session ? unreadPulseAlertCount : 0).catch(() => undefined);
  }, [session, unreadPulseAlertCount]);

  const pathname = usePathname();
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const prefix = pulseBadgeLabel ? `(${pulseBadgeLabel}) ` : '';
    const titles: Record<string, string> = {
      events: `${prefix}radious | Events`,
      map: `${prefix}radious | Map`,
      activity: `${prefix}radious | Pulse`,
      profile: `${prefix}radious | Profile`,
    };
    const tab = Object.keys(titles).find((name) => pathname.includes(name));
    document.title = tab ? titles[tab] : `${prefix}radious`;
  }, [pathname, pulseBadgeLabel]);

  const handlePagerStateChange = React.useCallback(
    (event: { data?: { state?: TabNavigationState<ParamListBase> } }) => {
      const nextState = event.data?.state;
      const nextRoute = nextState?.routes[nextState.index]?.name;
      if (!nextRoute || !(nextRoute in TAB_CONFIG) || lastTrackedRouteRef.current === nextRoute) {
        return;
      }
      if (lastTrackedRouteRef.current === null) {
        lastTrackedRouteRef.current = nextRoute;
        return;
      }
      lastTrackedRouteRef.current = nextRoute;
      void triggerSelectionHaptic();
      trackTabSwitched(nextRoute);
    },
    []
  );

  if (isHydrating) {
    return <View style={{ flex: 1, backgroundColor: DESIGN_TOKENS.colors.canvas }} />;
  }

  return (
    <View style={{ flex: 1 }}>
      {isDesktop ? <AppTopBar pulseBadgeLabel={pulseBadgeLabel} /> : null}
      <TabSwipeNavigationLayer
        activeRoute={activeSwipeRoute}
        enabled={!isDesktop && isTabRoute && activeSwipeRoute === 'map'}>
        <Tabs
          screenListeners={{
            state: handlePagerStateChange,
          }}
          screenOptions={{
            animation: 'none',
            freezeOnBlur: true,
            headerShown: false,
            lazy: false,
            sceneStyle: {
              backgroundColor: DESIGN_TOKENS.colors.canvas,
            },
            tabBarStyle: { display: 'none' },
          }}
          tabBar={
            isDesktop
              ? () => null
              : (props) =>
                  shouldKeepTabBarMounted ? (
                    <AppTabBar
                      {...props}
                      pulseBadgeLabel={pulseBadgeLabel}
                    />
                  ) : null
          }>
          <Tabs.Screen
            name="events"
            options={{
              title: pulseBadgeLabel ? `(${pulseBadgeLabel}) radious | Events` : 'radious | Events',
            }}
          />
          <Tabs.Screen
            name="map"
            options={{
              title: pulseBadgeLabel ? `(${pulseBadgeLabel}) radious | Map` : 'radious | Map',
            }}
          />
          <Tabs.Screen
            name="activity"
            options={{
              title: pulseBadgeLabel ? `(${pulseBadgeLabel}) radious | Pulse` : 'radious | Pulse',
            }}
          />
          <Tabs.Screen
            name="profile"
            options={{
              title: pulseBadgeLabel
                ? `(${pulseBadgeLabel}) radious | Profile`
                : 'radious | Profile',
            }}
          />
        </Tabs>
      </TabSwipeNavigationLayer>
    </View>
  );
}
